import { createContext, type PropsWithChildren, useContext, useMemo } from 'react';

import { type WorkspaceMemberResponseWithRoles } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useGetWorkspaceMembers } from '~/swr-hooks/members/useGetWorkspaceMembers';
import { isDevOrTestStage } from '~/swr-hooks/utils';

type WorkspaceMembersProviderProps = PropsWithChildren<object>;

export type WorkspaceMembersProviderContextValue = {
  data: WorkspaceMemberResponseWithRoles[] | undefined;
  error?: unknown;
};

const defaultValue: WorkspaceMembersProviderContextValue = {
  data: undefined,
};

const WorkspaceMembersProviderContext = createContext<WorkspaceMembersProviderContextValue>(defaultValue);

export const WorkspaceMembersProvider = ({ children }: WorkspaceMembersProviderProps) => {
  const { data, error } = useGetWorkspaceMembers();

  const value = useMemo(() => ({ data, error }), [data, error]);

  return <WorkspaceMembersProviderContext.Provider value={value}>{children}</WorkspaceMembersProviderContext.Provider>;
};

export const useWorkspaceMembersContext = () => {
  const context = useContext(WorkspaceMembersProviderContext);

  if (context === defaultValue) {
    const error = 'WorkspaceMembersProviderContext used outside of WorkspaceMembersProvider';

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: error,
      });
    }
  }

  return context;
};
